import { FieldGlobalAttributes } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { uniqBy } from 'lodash';
import { FormattingGroup } from '../types';
import { SelectOptionWithDescription, toOption } from './utils';
import { getCustomFieldsFormattingGroupIfAnyFieldIsAvailableOrNull } from './getCustomFieldsFormattingGroup';
import { DEFAULT_FORMATTING_GROUP_LABEL } from '../../../consts';
import {
  COMPONENT_BUILT_IN_FIELD_DEFS,
  toBuiltInFieldFormatting,
} from '../utils';
import { Locale } from '@ardoq/locale';
import type { I18nLocale } from '@ardoq/i18n';

type Options = {
  i18nLocale: I18nLocale;
  relatedFields: FieldGlobalAttributes[];
  componentTypeNames: string[];
  isNewConditionalFormatting: boolean;
  locale: Locale;
};

const getDefaultFieldsFormattingGroup = (
  i18nLocale: I18nLocale,
  componentTypeOptions: SelectOptionWithDescription[],
  isNewConditionalFormatting: boolean
): FormattingGroup => {
  return {
    label: DEFAULT_FORMATTING_GROUP_LABEL,
    filters: COMPONENT_BUILT_IN_FIELD_DEFS.map(def =>
      toBuiltInFieldFormatting(
        def,
        def.label(i18nLocale, isNewConditionalFormatting),
        componentTypeOptions
      )
    ),
  };
};

export const composeComponentFormattingGroups = ({
  i18nLocale,
  relatedFields,
  componentTypeNames,
  isNewConditionalFormatting,
  locale,
}: Options): FormattingGroup[] => {
  // modify the global property of the mock fields, otherwise composeFiltersFromFields will filter them out.
  // we want to see these fields in the query builder.
  const relatedComponentFields = relatedFields.map(field => ({
    ...field,
    global: true,
  }));
  const componentTypeOptions = uniqBy(
    componentTypeNames.map(name => toOption(name)),
    'value'
  );

  const defaultFieldsFormattingGroup = getDefaultFieldsFormattingGroup(
    i18nLocale,
    componentTypeOptions,
    isNewConditionalFormatting
  );
  const customFieldsFormattingGroup =
    getCustomFieldsFormattingGroupIfAnyFieldIsAvailableOrNull(
      relatedComponentFields,
      isNewConditionalFormatting,
      locale
    );

  return [defaultFieldsFormattingGroup, customFieldsFormattingGroup].filter(
    isTruthy
  );
};
